// COP — Account / Billing tab (Wave 2C.2).
//
// Reads the caller's `subscriptions` row + joined plan via the shared
// `useSubscription` hook. Renders:
//   * Plan card — display_name, status badge, trial countdown, próximo cobro
//     or "acceso hasta" depending on whether the subscription is Stripe-
//     managed, usage counters vs plan caps.
//   * Cancel-subscription Dialog — only shown for Stripe-managed `active`
//     subscriptions. Sets `cancel_at_period_end = true` via the
//     `cancelSubscription` edge function; the webhook mirrors the change
//     back into the local row.
//   * `?checkout=success` toast + banner — Stripe redirects here on
//     successful Checkout. The webhook may take a few seconds to fire, so
//     we show a "Procesando tu pago…" banner and force a refetch.
//   * Facturación placeholder — CFDI is deferred (decision COP-10); we keep
//     the layout slot but replace the form with a "próximamente" notice.
//   * Historial de facturas placeholder — Stripe customer portal lands in
//     Wave 2C.3.

import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { useSubscription } from '@/hooks/useSubscription';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import type { Plan, Subscription, SubscriptionStatus } from '@/types';

// ---------------------------------------------------------------------------
// Helpers — currency / date / status formatting kept top-level so JSX stays
// flat and the unit suite can exercise them directly if added later.
// ---------------------------------------------------------------------------

const mxnFormatter = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
  maximumFractionDigits: 0,
});

function formatMxn(cents: number): string {
  return mxnFormatter.format(cents / 100);
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-MX', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

/** Render a byte count using B / KB / MB / GB. Plain text — no JSX. */
function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

interface StatusBadgeMeta {
  label: string;
  className: string;
}

const STATUS_META: Record<SubscriptionStatus, StatusBadgeMeta> = {
  trialing: { label: 'En prueba', className: 'bg-green-100 text-green-800' },
  active: { label: 'Activo', className: 'bg-green-100 text-green-800' },
  past_due: { label: 'Pago atrasado', className: 'bg-yellow-100 text-yellow-800' },
  expired_readonly: {
    label: 'Solo lectura',
    className: 'bg-yellow-100 text-yellow-800',
  },
  canceled: { label: 'Cancelado', className: 'bg-red-100 text-red-800' },
  archived: { label: 'Archivado', className: 'bg-red-100 text-red-800' },
  unpaid: { label: 'Sin pagar', className: 'bg-gray-200 text-gray-800' },
};

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return null;
  const diff = target - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

/** Render a usage row "X / Y" or "X / Ilimitado". */
function UsageRow({
  label,
  used,
  cap,
  formatter = (v) => v.toLocaleString('es-MX'),
}: {
  label: string;
  used: number;
  cap: number | null;
  formatter?: (n: number) => string;
}) {
  const capLabel = cap === null ? 'Ilimitado' : formatter(cap);
  const pct = cap && cap > 0 ? Math.min(100, Math.round((used / cap) * 100)) : null;
  return (
    <div>
      <div className="flex justify-between items-center text-sm mb-1">
        <span className="font-medium text-gray-700">{label}</span>
        <span className="text-gray-600">
          {formatter(used)} / {capLabel}
        </span>
      </div>
      {pct !== null && (
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-brand-primary rounded-full"
            style={{
              width: `${pct}%`,
              backgroundColor: pct > 90 ? '#dc2626' : '#3d5a80',
            }}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cancel subscription helper — calls the edge function and refetches.
// ---------------------------------------------------------------------------

interface CancelResult {
  ok: boolean;
  cancel_at: string | null;
  error?: string;
}

async function callCancelSubscription(): Promise<CancelResult> {
  const { data, error } = await supabase.functions.invoke('cancelSubscription', {
    body: {},
  });
  if (error) {
    return { ok: false, cancel_at: null, error: error.message };
  }
  return (data as CancelResult) ?? { ok: false, cancel_at: null };
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const Account: React.FC = () => {
  const { subscription, plan, isLoading, isError, refetch } = useSubscription();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [cancelOpen, setCancelOpen] = React.useState(false);
  const [cancelBusy, setCancelBusy] = React.useState(false);

  // ?checkout=success handling — one-shot toast + banner + refetch.
  const successHandledRef = React.useRef(false);
  const checkoutSuccess = searchParams.get('checkout') === 'success';
  React.useEffect(() => {
    if (!checkoutSuccess || successHandledRef.current) return;
    successHandledRef.current = true;
    toast({
      title: '¡Gracias por tu compra!',
      description:
        'Procesando tu pago. Esto puede tomar unos segundos en reflejarse.',
    });
    void queryClient.invalidateQueries({ queryKey: ['my-subscription'] });
    void refetch();
    // We intentionally leave the ?checkout=success param in the URL while the
    // banner is up so a hard refresh keeps the visual cue. Clearing it after
    // 8s gives the webhook time to fire and the user time to read.
    const t = setTimeout(() => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('checkout');
        return next;
      });
    }, 8000);
    return () => clearTimeout(t);
  }, [checkoutSuccess, queryClient, refetch, setSearchParams, toast]);

  const handleConfirmCancel = async () => {
    setCancelBusy(true);
    const result = await callCancelSubscription();
    setCancelBusy(false);
    if (!result.ok) {
      toast({
        variant: 'destructive',
        title: 'No pudimos cancelar tu suscripción',
        description:
          result.error ?? 'Inténtalo de nuevo en unos minutos o contáctanos.',
      });
      return;
    }
    setCancelOpen(false);
    toast({
      title: 'Cancelación programada',
      description: result.cancel_at
        ? `Tu suscripción terminará el ${formatDate(result.cancel_at)}.`
        : 'Tu suscripción terminará al final del periodo actual.',
    });
    void queryClient.invalidateQueries({ queryKey: ['my-subscription'] });
    void refetch();
  };

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-gray-900">Cuenta y Facturación</h1>

      {checkoutSuccess && (
        <div
          className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 flex items-center gap-2"
          role="status"
        >
          <Loader2 className="w-4 h-4 animate-spin" />
          Procesando tu pago. Esto puede tomar unos segundos.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Plan actual */}
        <Card>
          <CardHeader>
            <CardTitle>Plan Actual</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isLoading ? (
              <PlanCardSkeleton />
            ) : isError || !subscription || !plan ? (
              <NoSubscriptionState onChangePlan={() => navigate('/precios')} />
            ) : (
              <PlanSummary
                subscription={subscription}
                plan={plan}
                onChangePlan={() => navigate('/precios')}
                onOpenCancel={() => setCancelOpen(true)}
              />
            )}
          </CardContent>
        </Card>

        {/* Facturación placeholder (CFDI deferred) */}
        <Card>
          <CardHeader>
            <CardTitle>Facturación</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">
              Facturación con CFDI 4.0 próximamente. Mientras tanto, recibirás un
              comprobante por correo electrónico de cada pago realizado a través
              de Stripe.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Historial de facturas placeholder */}
      <Card>
        <CardHeader>
          <CardTitle>Historial de facturas</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-500 space-y-2">
            <p>Aún no hay facturas.</p>
            <p className="text-xs">
              El acceso al portal de cliente Stripe estará disponible próximamente.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Cancel dialog */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Cancelar suscripción?</DialogTitle>
            <DialogDescription>
              Conservarás el acceso hasta el final de tu periodo pagado. Puedes
              reactivar tu suscripción en cualquier momento antes de esa fecha.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCancelOpen(false)}
              disabled={cancelBusy}
            >
              Conservar suscripción
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmCancel}
              disabled={cancelBusy}
            >
              {cancelBusy ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Cancelando…
                </>
              ) : (
                'Sí, cancelar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

const PlanCardSkeleton: React.FC = () => (
  <div className="space-y-4" aria-busy="true">
    <div className="flex justify-between items-center">
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-6 w-20" />
    </div>
    <Skeleton className="h-4 w-3/4" />
    <Skeleton className="h-4 w-1/2" />
    <Skeleton className="h-2 w-full" />
    <Skeleton className="h-2 w-full" />
    <Skeleton className="h-10 w-full" />
  </div>
);

const NoSubscriptionState: React.FC<{ onChangePlan: () => void }> = ({
  onChangePlan,
}) => (
  <div className="space-y-4">
    <p className="text-sm text-gray-600">
      No encontramos una suscripción activa para tu cuenta. Si crees que es un
      error, recarga la página o contáctanos.
    </p>
    <Button className="w-full" onClick={onChangePlan}>
      Ver planes
    </Button>
  </div>
);

interface PlanSummaryProps {
  subscription: Subscription;
  plan: Plan;
  onChangePlan: () => void;
  onOpenCancel: () => void;
}

const PlanSummary: React.FC<PlanSummaryProps> = ({
  subscription,
  plan,
  onChangePlan,
  onOpenCancel,
}) => {
  const meta = STATUS_META[subscription.status] ?? STATUS_META.unpaid;

  const isTrialing = subscription.status === 'trialing';
  const trialDaysLeft = isTrialing ? daysUntil(subscription.trial_end) : null;
  const isStripeManaged = !!subscription.stripe_subscription_id;
  const showNextBilling =
    subscription.status === 'active' && !!subscription.current_period_end;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <span className="font-medium">Plan:</span>
        <span className="text-gray-900 font-semibold">{plan.display_name}</span>
      </div>
      <div className="flex justify-between items-center">
        <span className="font-medium">Estado:</span>
        <Badge className={meta.className}>{meta.label}</Badge>
      </div>
      {plan.price_mxn_cents > 0 && (
        <div className="flex justify-between items-center">
          <span className="font-medium">Precio:</span>
          <span className="text-gray-900">
            {formatMxn(plan.price_mxn_cents)}
            {plan.billing_cycle === 'MONTH'
              ? ' / mes'
              : plan.billing_cycle === 'YEAR'
              ? ' / año'
              : ''}
          </span>
        </div>
      )}

      {isTrialing && trialDaysLeft !== null && (
        <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-blue-900">
          Tu prueba termina en{' '}
          <strong>
            {trialDaysLeft} {trialDaysLeft === 1 ? 'día' : 'días'}
          </strong>
          .
        </div>
      )}

      {showNextBilling && (
        <div className="flex justify-between items-center">
          <span className="font-medium">
            {isStripeManaged ? 'Próximo cobro:' : 'Acceso hasta:'}
          </span>
          <span className="text-gray-900">
            {formatDate(subscription.current_period_end)}
          </span>
        </div>
      )}

      {/* Usage counters */}
      <div className="space-y-3 pt-2 border-t border-gray-100">
        <UsageRow
          label="Eventos activos"
          used={subscription.events_used_in_period}
          cap={plan.active_events_cap}
        />
        <UsageRow
          label="Mensajes WhatsApp"
          used={subscription.whatsapp_used_in_period}
          cap={plan.whatsapp_messages_per_period}
        />
        <UsageRow
          label="Almacenamiento"
          used={subscription.storage_used_bytes}
          cap={plan.storage_bytes}
          formatter={formatBytes}
        />
      </div>

      <Button className="w-full" onClick={onChangePlan}>
        Cambiar de plan
      </Button>

      {subscription.status === 'active' && isStripeManaged && (
        <Button
          variant="outline"
          className="w-full text-red-600 border-red-200 hover:bg-red-50"
          onClick={onOpenCancel}
        >
          Cancelar suscripción
        </Button>
      )}
    </div>
  );
};
